/**
 * DiscoveredPropertiesQueue — очередь новых (неизвестных) свойств из данных
 * (matching-v2 §D.3), BE GET/POST /datasets/{id}/matching/discovered-properties*.
 *
 * Показывает колонки-кандидаты из raw/enriched данных, которые НЕ покрыты
 * шаблоном / ручными свойствами. Кнопки:
 *   - Add (поштучно) → POST decide {action:'add'} → свойство становится
 *     manual (only_mine) на датасете.
 *   - Add all → батч add.
 *   - Skip (поштучно) → POST decide {action:'skip'} → per-dataset blacklist
 *     (Dataset.raw_metadata.matching.skipped_properties) — больше не показывается.
 *   - Skip all → батч skip.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Plus, X } from 'lucide-react';

import {
  useDecideDiscoveredProperties,
  useDiscoveredProperties,
} from '@/hooks/useMatching';

export interface DiscoveredPropertiesQueueProps {
  datasetId: string | null;
}

export function DiscoveredPropertiesQueue({
  datasetId,
}: DiscoveredPropertiesQueueProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useDiscoveredProperties(datasetId);
  const decide = useDecideDiscoveredProperties(datasetId);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const items = data?.items ?? [];

  async function decideOne(key: string, action: 'add' | 'skip') {
    setBusyKey(key);
    try {
      await decide.mutateAsync([{ key, action }]);
    } finally {
      setBusyKey(null);
    }
  }

  async function decideAll(action: 'add' | 'skip') {
    decide.mutate(items.map((i) => ({ key: i.key, action })));
  }

  if (!datasetId || isLoading) return null;
  if (items.length === 0) return null;

  return (
    <section className="card space-y-2" data-testid="discovered-properties">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-surface-400 flex items-center gap-2">
          <Eye className="w-4 h-4" />
          {t('process.matching.discovered.title', {
            defaultValue: 'New properties found',
          })}
        </h2>
        <span className="text-xs text-surface-500">{items.length}</span>
      </div>

      <p className="text-xs text-surface-500">
        {t('process.matching.discovered.hint', {
          defaultValue:
            'These columns exist in your data but are not covered by the template. Add them as manual properties or skip — skipped ones are remembered for this dataset.',
        })}
      </p>

      <div className="flex flex-wrap gap-1.5">
        {items.map((item) => (
          <span
            key={item.key}
            className="flex items-center gap-2 text-xs px-2 py-1 rounded-md border border-surface-700 bg-surface-900 text-surface-300"
            data-testid={`discovered-item-${item.key}`}
          >
            <span className="font-mono">{item.key}</span>
            <span className="text-[10px] text-surface-500">
              {t('process.matching.discovered.frequency', {
                defaultValue: 'rows: {{n}}',
                n: item.frequency,
              })}
            </span>
            <button
              type="button"
              onClick={() => decideOne(item.key, 'add')}
              disabled={decide.isPending || busyKey !== null}
              title={t('process.matching.discovered.add', {
                defaultValue: 'Add as manual property',
              })}
              className="text-success hover:text-success-tint transition-colors disabled:opacity-40"
              aria-label={t('process.matching.discovered.add', {
                defaultValue: 'Add',
              })}
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => decideOne(item.key, 'skip')}
              disabled={decide.isPending || busyKey !== null}
              title={t('process.matching.discovered.skip', {
                defaultValue: 'Skip (blacklist this dataset)',
              })}
              className="text-surface-500 hover:text-danger transition-colors disabled:opacity-40"
              aria-label={t('process.matching.discovered.skip', {
                defaultValue: 'Skip',
              })}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => decideAll('add')}
          disabled={decide.isPending}
          className="btn-secondary text-xs px-3 py-1"
        >
          {t('process.matching.discovered.addAll', {
            defaultValue: 'Add all',
          })}
        </button>
        <button
          type="button"
          onClick={() => decideAll('skip')}
          disabled={decide.isPending}
          className="btn-secondary text-xs px-3 py-1"
        >
          {t('process.matching.discovered.skipAll', {
            defaultValue: 'Skip all',
          })}
        </button>
      </div>
    </section>
  );
}